/**
 * Framebuffer Object wrapper for the offscreen scene texture and the
 * velocity FBO (Sprint 3 motion blur).
 */

export interface FBO {
  fbo: WebGLFramebuffer;
  tex: WebGLTexture;
  width: number;
  height: number;
}

export interface VelocityInfo {
  /** "RG16F" or "RG8" depending on what the impl supports. */
  format: "RG16F" | "RG8";
  formatEnum: number;
  typeEnum: number;
}

export function createFBO(
  gl: WebGL2RenderingContext,
  width: number,
  height: number,
  internalFormat: number = gl.RGBA,
  format: number = gl.RGBA,
  type: number = gl.UNSIGNED_BYTE,
): FBO | null {
  const tex = gl.createTexture();
  if (!tex) return null;
  gl.bindTexture(gl.TEXTURE_2D, tex);
  gl.texImage2D(gl.TEXTURE_2D, 0, internalFormat, width, height, 0, format, type, null);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);

  const fbo = gl.createFramebuffer();
  if (!fbo) return null;
  gl.bindFramebuffer(gl.FRAMEBUFFER, fbo);
  gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, tex, 0);

  const status = gl.checkFramebufferStatus(gl.FRAMEBUFFER);
  if (status !== gl.FRAMEBUFFER_COMPLETE) {
    console.error("[nspp] FBO incomplete: 0x" + status.toString(16));
    gl.deleteFramebuffer(fbo);
    gl.deleteTexture(tex);
    return null;
  }

  gl.bindFramebuffer(gl.FRAMEBUFFER, null);
  return { fbo, tex, width, height };
}

export function createSceneFBO(
  gl: WebGL2RenderingContext,
  width: number,
  height: number,
): FBO | null {
  return createFBO(gl, width, height);
}

/**
 * Velocity FBO. RG16F preferred, RG8 fallback.
 * Returns the FBO and metadata so the rect FS can choose the right output.
 */
export function createVelocityFBO(
  gl: WebGL2RenderingContext,
  width: number,
  height: number,
): { fbo: FBO; info: VelocityInfo } | null {
  const RG16F = (gl as unknown as { RG16F?: number }).RG16F;
  const HALF_FLOAT = (gl as unknown as { HALF_FLOAT?: number }).HALF_FLOAT;

  if (RG16F !== undefined && HALF_FLOAT !== undefined) {
    const probe = createFBO(gl, width, height, RG16F, gl.RG, HALF_FLOAT);
    if (probe) {
      return {
        fbo: probe,
        info: { format: "RG16F", formatEnum: RG16F, typeEnum: HALF_FLOAT },
      };
    }
  }
  const fb = createFBO(gl, width, height, gl.RG8, gl.RG, gl.UNSIGNED_BYTE);
  if (!fb) return null;
  return {
    fbo: fb,
    info: { format: "RG8", formatEnum: gl.RG8, typeEnum: gl.UNSIGNED_BYTE },
  };
}

export function resizeFBO(gl: WebGL2RenderingContext, fbo: FBO, w: number, h: number): FBO {
  if (fbo.width === w && fbo.height === h) return fbo;
  gl.deleteFramebuffer(fbo.fbo);
  gl.deleteTexture(fbo.tex);
  return createFBO(gl, w, h) ?? fbo;
}
